import type { DashboardData } from '@/core/types/dashboard';
import { env } from '@/core/config/env';
import type { Lead, LeadPriority, LeadStatus } from '@/core/types/lead';
import { toIsoDay } from '@/core/utils/date';
import { leadsApi } from './leadsApi';
import { httpClient } from './httpClient';

const statusLabels: Record<LeadStatus, string> = {
  new: 'جدید',
  working: 'در حال پیگیری',
  qualified: 'واجد شرایط',
  converted: 'تبدیل‌شده',
  disqualified: 'ردشده',
  archived: 'بایگانی',
};

const priorityLabels: Record<LeadPriority, string> = {
  low: 'کم',
  normal: 'عادی',
  high: 'بالا',
  urgent: 'فوری',
};

const statusOrder: LeadStatus[] = [
  'new',
  'working',
  'qualified',
  'converted',
  'disqualified',
  'archived',
];
const priorityOrder: LeadPriority[] = ['urgent', 'high', 'normal', 'low'];

const countBy = <T extends string>(items: Lead[], selector: (lead: Lead) => T): Map<T, number> => {
  const counts = new Map<T, number>();
  items.forEach((lead) => {
    const key = selector(lead);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
};

const createTrend = (leads: Lead[]): Array<{ date: string; created: number }> => {
  const days = Array.from({ length: 14 }, (_, offset) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (13 - offset));
    return toIsoDay(date);
  });
  const counts = new Map(days.map((day) => [day, 0]));
  leads.forEach((lead) => {
    const day = toIsoDay(lead.createdAtUtc);
    if (counts.has(day)) counts.set(day, (counts.get(day) || 0) + 1);
  });
  return days.map((date) => ({ date, created: counts.get(date) || 0 }));
};

export const dashboardApi = {
  async getDashboard(ownerMembershipId?: string): Promise<DashboardData> {
    if (!env.isDemo) {
      const query = ownerMembershipId
        ? `?ownerMembershipId=${encodeURIComponent(ownerMembershipId)}`
        : '';
      return (await httpClient.get<DashboardData>(`/api/v1/reports/dashboard/leads${query}`)).data;
    }

    const baseQuery = ownerMembershipId ? { ownerMembershipId } : {};
    const statusRequests = statusOrder.map((status) =>
      leadsApi.list({ ...baseQuery, status, page: 1, pageSize: 1 }),
    );

    const [all, recent, ...statusPages] = await Promise.all([
      leadsApi.list({ ...baseQuery, page: 1, pageSize: 100 }),
      leadsApi.list({ ...baseQuery, page: 1, pageSize: 10 }),
      ...statusRequests,
    ]);

    const statusCounts = new Map<LeadStatus, number>();
    statusOrder.forEach((status, index) => statusCounts.set(status, statusPages[index].totalCount));
    const priorityCounts = countBy(all.items, (lead) => lead.priority);
    const sources = countBy(all.items, (lead) => lead.source || 'نامشخص');
    const urgentLeads = all.items
      .filter((lead) => lead.priority === 'urgent' && !['converted', 'archived'].includes(lead.status))
      .slice(0, 6);
    const averageScore = all.items.length
      ? Math.round(all.items.reduce((sum, lead) => sum + lead.score, 0) / all.items.length)
      : 0;
    const convertedCount = statusCounts.get('converted') || 0;
    const qualifiedCount = statusCounts.get('qualified') || 0;
    const denominator = Math.max(1, all.totalCount - (statusCounts.get('archived') || 0));

    return {
      kpis: {
        total: all.totalCount,
        newCount: statusCounts.get('new') || 0,
        workingCount: statusCounts.get('working') || 0,
        qualifiedCount,
        convertedCount,
        urgentCount: priorityCounts.get('urgent') || 0,
        unassignedCount: all.items.filter((lead) => !lead.ownerMembershipId).length,
        averageScore,
        conversionRate: (convertedCount + qualifiedCount * 0.35) / denominator,
      },
      recentLeads: recent.items,
      urgentLeads,
      statusDistribution: statusOrder.map((key) => ({
        key,
        label: statusLabels[key],
        value: statusCounts.get(key) || 0,
      })),
      priorityDistribution: priorityOrder.map((key) => ({
        key,
        label: priorityLabels[key],
        value: priorityCounts.get(key) || 0,
      })),
      sourceDistribution: [...sources.entries()]
        .map(([key, value]) => ({ key, label: key, value }))
        .sort((left, right) => right.value - left.value)
        .slice(0, 6),
      trend: createTrend(all.items),
      sampledLeadCount: all.items.length,
      generatedAt: new Date().toISOString(),
    };
  },
};
